import type { PublisherActivityEntry } from '@planuze/admin-contracts';
import {
  EmptyState,
  StatusBadge,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@planuze/ui';
import { History } from 'lucide-react';
import { usePublishersContent } from '../../content/sections/publishers.js';
import { fmtDateTime } from '../../lib/format.js';
import { CopyableId } from '../copyable-id.js';

/* Tabela da trilha de atividade de um publisher (aba Atividade — ADR-0466). Molde do `audit-table`:
   presentacional puro (a página é dona da infinite query + "carregar mais"). Resolve "publisher sem
   histórico" no backoffice. Fallback para a string crua (via `?? entry.action`) mantém a tabela
   resiliente a ações novas antes de ganharem rótulo em `content/sections/publishers.ts`. */

export function PublisherActivityTable({
  items,
}: {
  readonly items: readonly PublisherActivityEntry[];
}) {
  const copy = usePublishersContent();
  if (items.length === 0) {
    return (
      <EmptyState
        icon={<History className="size-6" />}
        title={copy.activityTable.emptyTitle}
        description={copy.activityTable.emptyDescription}
      />
    );
  }
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>{copy.activityTable.headers.when}</TableHead>
          <TableHead>{copy.activityTable.headers.action}</TableHead>
          <TableHead>{copy.activityTable.headers.target}</TableHead>
          <TableHead>{copy.activityTable.headers.actor}</TableHead>
          <TableHead>{copy.activityTable.headers.ip}</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {items.map((entry) => (
          <TableRow key={entry.id}>
            <TableCell className="whitespace-nowrap text-muted-foreground">
              {fmtDateTime(entry.ts)}
            </TableCell>
            <TableCell>
              <StatusBadge variant="neutral">
                {copy.activityTable.actionLabels[entry.action] ?? entry.action}
              </StatusBadge>
            </TableCell>
            <TableCell>
              <div className="flex flex-col gap-0.5">
                <span className="text-xs text-muted-foreground">
                  {copy.activityTable.targetLabels[entry.targetType] ?? entry.targetType}
                </span>
                <span className="font-mono text-sm text-foreground">{entry.targetId}</span>
              </div>
            </TableCell>
            <TableCell>
              {entry.actorUserId === null ? (
                <span className="text-muted-foreground">—</span>
              ) : (
                <CopyableId value={entry.actorUserId} label={copy.activityTable.actorIdLabel} />
              )}
            </TableCell>
            <TableCell className="font-mono text-xs text-muted-foreground">
              {entry.ipAddress ?? '—'}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
